import React, { useState, useEffect } from 'react';

const CommonTable = ({
  columns,
  data,
  itemsPerPageOptions = [10, 15, 25, 50, 100, 'All'],
  defaultItemsPerPage = 15,
  showPagination = true
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(String(defaultItemsPerPage));

  useEffect(() => {
    setCurrentPage(1);
  }, [data]);

  const totalItems = data.length;
  const isShowAll = itemsPerPage === 'All';
  const parsedItemsPerPage = isShowAll ? totalItems : parseInt(itemsPerPage, 10) || defaultItemsPerPage;
  const totalPages = isShowAll ? 1 : Math.ceil(totalItems / parsedItemsPerPage);
  const startIndex = isShowAll ? 0 : (currentPage - 1) * parsedItemsPerPage;
  const endIndex = isShowAll ? totalItems : startIndex + parsedItemsPerPage;
  const currentData = data.slice(startIndex, endIndex);

  // Debugging info to help diagnose pagination issues in dev
  // (will be a no-op in production consoles if not opened)
  // eslint-disable-next-line no-console
  console.debug('CommonTable pagination', {
    totalItems,
    itemsPerPage,
    parsedItemsPerPage,
    totalPages,
    currentPage,
    startIndex,
    endIndex,
  });

  // Ensure currentPage is within bounds when totalPages changes
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages > 0 ? totalPages : 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalPages]);

  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= maxPagesToShow; i++) {
          pages.push(i);
        }
      } else if (currentPage >= totalPages - 2) {
        for (let i = totalPages - maxPagesToShow + 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        for (let i = currentPage - 2; i <= currentPage + 2; i++) {
          pages.push(i);
        }
      }
    }
    return pages;
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(String(newItemsPerPage));
    setCurrentPage(1);
  };

  return (
    <div
      className="card"
      style={{
        borderRadius: '8px',
        border: '1px solid #e3e6ef',
        overflow: 'hidden',
      }}
    >
      <div className="card-body p-0">
        <div className="table-responsive">
          <table
            className="table mb-0"
          >
            <thead>
              <tr>
                {columns.map((col, index) => (
                  <th
                    key={index}
                    className="fw-semibold p-2 letterSpacing-1"
                    style={{
                      backgroundColor: '#F6F7F8',
                      color: '#858585',
                      border: '2px #D6DBDD',
                      // padding: '12px 8px',
                      // letterSpacing: '0.5px',
                    }}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {currentData.length > 0 ? (
                currentData.map((row, i) => (
                  <tr
                    key={i}
                  >
                    {columns.map((col, j) => (
                      <td
                        key={j}
                      >
                        {row[col.key]}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td className="p-0"
                    colSpan={columns.length}
                  >
                    <div className="d-flex flex-column align-items-center">
                      <i className="fas fa-inbox fa-2x mb-3"></i>
                      <span>No data available</span>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination and entries info */}
      {showPagination && (
        <div
          className="d-flex justify-content-between align-items-center px-2 py-2"
        >
          {/* Show entries dropdown */}
          <div className="d-flex align-items-center">
            <span className="me-2">Show</span>
            <select
              className="form-select form-select-sm"
              style={{
                width: '80px',
                fontSize: '0.95rem',
                border: '1px solid #e3e6ef',
                borderRadius: '6px',
                padding: '2px 8px',
              }}
              value={itemsPerPage}
              onChange={e => handleItemsPerPageChange(e.target.value)}
            >
              {itemsPerPageOptions.map(option => (
                <option key={option} value={String(option)}>{option === 'All' ? 'All' : option}</option>
              ))}
            </select>
            <span className="ms-2">Entries</span>
          </div>

          {/* Pagination Controls */}
          {totalPages >= 1 && (
            <nav aria-label="Table pagination">
              <ul className="pagination pagination-sm mb-0">
                <li className={`page-item ${currentPage === 1 ? 'enable' : ''}`}>
                  <button
                    className="page-link"
                    style={{
                      border: 'none',
                      background: 'transparent',
                      color: currentPage === 1 ? '#b0b7c3' : '#252525',
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    }}
                    onClick={() => handlePageChange(currentPage - 1)}
                    enable={currentPage === 1}
                  >
                    &lt; Previous
                  </button>
                </li>
                {getPageNumbers().map(page => (
                  <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                    <button
                      className="page-link mx-1"
                      style={{
                        border: 'none',
                        background: currentPage === page ? '#638225' : 'transparent',
                        color: currentPage === page ? '#fff' : '#252525',
                        borderRadius: '6px',
                        fontWeight: currentPage === page ? 600 : 400,
                      }}
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </button>
                  </li>
                ))}
                <li className={`page-item ${currentPage === totalPages ? 'enable' : ''}`}>
                  <button
                    className="page-link"
                    style={{
                      border: 'none',
                      background: 'transparent',
                      color: currentPage === totalPages ? '#b0b7c3' : '#252525',
                      cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    }}
                    onClick={() => handlePageChange(currentPage + 1)}
                    enable={currentPage === totalPages}
                  >
                    Next &gt;
                  </button>
                </li>
              </ul>
            </nav>
          )}
        </div>
      )}
    </div>
  );
};

export default CommonTable;